import React, { useMemo, useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { connect } from "react-redux";
import { Alert } from "services/Alert";

import { getReferencesForReports } from "actions/references";
import { ButtonPrimary } from "components/base/BaseButton";
import { checkPermissionsFor } from "store/storeHelpers";
import { getE212DropdownList } from "actions/e212";
import {
    createExceptionsWrongLangTraffic,
    modifyExceptionsWrongLangTraffic,
    removeExceptionsWrongLangTraffic,
    getExceptionsWrongLangTrafficList,
    setExceptionsWrongLangTrafficLoading
} from "actions/exceptions_wrong_lang";
import { useWindowWidth } from "hooks";
import { deleteEmpty } from "utils";
import {
    EXCEPTIONS_WRONG_LANG_CREATE_API,
    EXCEPTIONS_WRONG_LANG_MODIFY_API,
    EXCEPTIONS_WRONG_LANG_REMOVE_API
} from "const/apiMethods";
import { FilterSectionFlexboxGrid, StyledItemFitWidth, StyledItemAutoMargin } from "components/base/FitContainers";
import PanelLayout from "components/base/PanelLayout";
import PageHeader from "components/base/PageHeader";
import ExceptionsWrongLangCreateModal from "./ExceptionsWrongLangCreateModal";
import ExceptionsWrongLangFilters from "./ExceptionsWrongLangFilters";
import ExceptionsWrongLangTable from "./ExceptionsWrongLangTable";

const defaultFilter = {};


const ExceptionsWrongLangPage = ({
    data,
    loading,

    e212List,
    dialerList,
    supplierList,

    referenceLoading,

    setLoading,
    createApiFunc,
    modifyApiFunc,
    removeApiFunc,

    getExceptionList,
    getE212DropdownList,
    getReferencesForReports
}) => {

    const location = useLocation();

    const [filter, setFilter] = useState(defaultFilter);
    const [exceptionMap, setExceptionMap] = useState(new Map());
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [initialCreateValues, setInitialCreateValues] = useState(null);

    const [editKeys, setEditKeys] = useState([]);

    const windowWidth = useWindowWidth();
    const isMobile = windowWidth < 767;


    const mccMncList = e212List && e212List.length ? e212List.map(value => {
        return {
            ...value,
            nameMccMnc: `${value.mccmnc_name} (${value.mcc_mnc})`
        };
    }) : [];

    const dataFilteredList = useMemo(() => {
        if (!data)
            return [];

        const toLower = (value) => (value ?? "").toString().toLowerCase().trim();

        const filterSender = toLower(filter.senderid);
        const filterLang = toLower(filter.language);
        const filterMccMnc = toLower(filter.mcc_mnc);

        const filteredData = data.filter(item => {

            if (filter.do_not_send != null && filter.do_not_send !== item.do_not_send) {
                return false;
            }

            if (filter.supplier_id && filter.supplier_id !== item.supplier_id) {
                return false;
            }

            if (filter.dialer_id && filter.dialer_id !== item.dialer_id) {
                return false;
            }

            if (filterSender) {
                const itemSender = toLower(item.senderid);
                if (!itemSender) return false;
                if (!itemSender.includes(filterSender)) return false;
            }

            if (filterLang) {
                const itemLang = toLower(item.language);
                if (!itemLang) return false;
                if (!itemLang.includes(filterLang)) return false;
            }

            if (filterMccMnc) {
                const itemMccMnc = toLower(item.mcc_mnc);
                if (!itemMccMnc) return false;
                if (!itemMccMnc.includes(filterMccMnc)) return false;
            }

            return true;
        });

        return filteredData;
    }, [data, filter]);

    useEffect(() => {
        getExceptionList();
        getE212DropdownList({}, true);
        getReferencesForReports();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onDataUpdate = () => {
        getExceptionList(filter);
    };


    const parseCreateParams = (searchParams) => {
        const pick = (key) => searchParams.get(key);
        const bool = (v) => (v == null ? undefined : v === "true" || v === "1");

        // Whitelist the fields that your create modal understands
        const values = {
            senderid: pick("senderid"),
            language: pick("language"),
            supplier_id: pick("supplier_id"),
            mcc_mnc: pick("mcc_mnc"),
            dialer_id: pick("dialer_id"),
            do_not_send: bool(pick("do_not_send"))
        };

        // Remove undefined keys
        return Object.fromEntries(Object.entries(values).filter(([, v]) => v !== undefined && v !== ""));
    };


    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const shouldOpen = searchParams.get("create-exception");

        if (shouldOpen === "true" || shouldOpen === "1") {
            const prefill = parseCreateParams(searchParams);

            setInitialCreateValues(prefill);
            setShowCreateModal(true);


            searchParams.delete("create-exception");

            const paramsKeyList = ["senderid", "language", "supplier_id", "mcc_mnc", "dialer_id", "do_not_send"];

            paramsKeyList.forEach(k => searchParams.delete(k));

            window.history.replaceState({}, "", `${window.location.pathname}?${searchParams.toString()}`);
            // navigate({ pathname: location.pathname, search: searchParams.toString() }, { replace: true });
        }
    }, [location.search, location.pathname]);

    const onChangeFilters = (filterValue) => {
        const oldFilter = { ...filterValue };
        const newFilter = deleteEmpty(oldFilter);

        setFilter(newFilter);
    };


    const handleCreateException = async (params) => {
        const {
            supplier_id: supplierId,
            dialer_id: dialerId,
            ...restParams
        } = params;

        const finalParams = {
            target: {
                "sms.supplier_id": supplierId,
                dialer_id: dialerId
            },
            ...restParams
        };

        const response = await createApiFunc(finalParams);

        return response;
    };

    const handleModifyException = async (params, rowData) => {
        const {
            sms_language_exception_key,
            supplier_id: supplierId,
            dialer_id: dialerId,
            ...restParams
        } = params;

        const currentException = exceptionMap.get(sms_language_exception_key);

        const finalParams = {
            target: {
                "sms.supplier_id": supplierId,
                dialer_id: dialerId
            },
            sms_language_exception_key,
            do_not_send: currentException,
            ...restParams
        };

        try {
            const response = await modifyApiFunc(finalParams);

            if (response?.language_exception_key) {
                Alert.success("Exception modified!");
            }

            return response;
        } catch (error) {
            throw error;
        }
    };

    const handleRemoveException = async (key) => {
        try {
            const response = await removeApiFunc(key);

            if (response?.language_exception_key) {
                Alert.success("Exception removed!");
            }

            return response;
        } catch (error) {
            throw error;
        }
    };

    return (
        <PanelLayout>
            <PageHeader showService={false} title="Exceptions for SMS wrong language traffic" />
            <FilterSectionFlexboxGrid>
                <StyledItemFitWidth
                    isMobile={isMobile}
                >
                    <ExceptionsWrongLangFilters
                        loading={referenceLoading}
                        dialerList={dialerList}
                        mccMncList={mccMncList}
                        supplierList={supplierList}
                        defaultFilter={defaultFilter}
                        onChangeFilter={onChangeFilters}
                    />
                </StyledItemFitWidth>
                <StyledItemAutoMargin>
                    <ButtonPrimary
                        onClick={() => {
                            setShowCreateModal(true);
                        }}
                        disabled={!checkPermissionsFor(EXCEPTIONS_WRONG_LANG_CREATE_API)}
                    >
                        Create Exception
                    </ButtonPrimary>
                </StyledItemAutoMargin>
            </FilterSectionFlexboxGrid>
            <ExceptionsWrongLangTable
                data={dataFilteredList}
                loading={loading}
                mccMncList={mccMncList}
                dialerList={dialerList}
                supplierList={supplierList}
                exceptionMap={exceptionMap}
                editKeys={editKeys}
                setEditKeys={setEditKeys}

                setExceptionMap={setExceptionMap}

                onDataUpdate={onDataUpdate}
                // onRowCreate={handleCreateException}
                showFormCreate={false}
                onRowSubmit={handleModifyException}
                onRowRemove={handleRemoveException}
                // createPermission={checkPermissionsFor(EXCEPTIONS_WRONG_LANG_CREATE_API)}
                modifyPermission={checkPermissionsFor(EXCEPTIONS_WRONG_LANG_MODIFY_API)}
                removePermission={checkPermissionsFor(EXCEPTIONS_WRONG_LANG_REMOVE_API)}
            />
            {showCreateModal && (
                <ExceptionsWrongLangCreateModal
                    show={showCreateModal}
                    loading={loading}
                    referenceLoading={referenceLoading}
                    windowWidth={windowWidth}
                    dialerList={dialerList}
                    supplierList={supplierList}
                    mccMncList={mccMncList}
                    defaultValues={initialCreateValues}
                    handleCreateException={handleCreateException}
                    onClose={() => {
                        setShowCreateModal(false)
                        setInitialCreateValues(null);
                    }}
                    onSuccess={() => {
                        setShowCreateModal(false);
                        setInitialCreateValues(null);
                        onDataUpdate();
                    }}
                    setLoading={setLoading}
                />
            )}
        </PanelLayout>
    );
};

const mapStateToProps = ({ exceptions_wrong_lang, references, e212 }) => ({
    data: exceptions_wrong_lang.list,
    referenceLoading: references.reportLoading,
    loading: exceptions_wrong_lang.loading,
    dialerList: references["sms.dialer_list"],
    supplierList: references["sms.supplier_list"],
    e212List: e212.list
});

const mapDispatchToProps = {
    setLoading: setExceptionsWrongLangTrafficLoading,
    createApiFunc: createExceptionsWrongLangTraffic,
    modifyApiFunc: modifyExceptionsWrongLangTraffic,
    removeApiFunc: removeExceptionsWrongLangTraffic,

    getExceptionList: getExceptionsWrongLangTrafficList,
    getE212DropdownList,
    getReferencesForReports
};

export default connect(mapStateToProps, mapDispatchToProps)(ExceptionsWrongLangPage);
